import ParentCss from "../../style.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Checkbox, Input, Tab, Tabs, TagsInput, Tooltip } from "nlib/ui";
import { getAccountsData } from "selectors/accounts";
import { getActiveOrganization } from "selectors/organizations";
import {
  getCurrentQuickBooksRealmId,
  getSelectedBusinessClasses,
  getSelectedBusinessData,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Businesses from "utils/Businesses";
import BusinessesActions from "actions/BusinessesActions";
import Button from "nlib/ui/Button";
import Countries from "const/Countries";
import DatePickerInput from "nlib/common/DatePickerInput";
import DateTimeFormats from "const/DateTimeFormats";
import Dates from "utils/Dates";
import IntegrationServices from "const/IntegrationServices";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import RpaTransactionTypes from "const/RpaTransactionTypes";
import SelectUncategorizedCategoryNames from "nlib/common/SelectUncategorizedCategoryNames";
import Step from "../Step";
import Texts from "utils/Texts";
import classNames from "classnames";
import moment from "moment";

const TABS = {
  GENERAL: "general",
  PROCESSING: "processing",
  DOCUMENTS: "documents",
  AUTOMATION: "automation"
};

const RpaSettingsStep = ({ index, currentStep }) => {
  const dispatch = useDispatch();

  const { messages, uiTexts } = useSelector(getTextsData);

  const quickBooksBusiness = useSelector(getCurrentQuickBooksRealmId);

  const { appsumoValue, countryCode, ignoreUncategorizedTransactions } = useSelector(getActiveOrganization);

  const businessData = useSelector(getSelectedBusinessData);

  const {
    id: selectedBusinessId,
    extraData: { integrationService, xeroBookCloseDate, quickBooksBookCloseDate, rpaUpsellDate } = {},
    settings: {
      rpaClassEnabled: initialRpaClassEnabled = false,
      rpaLocationEnabled: initialRpaLocationEnabled = false,
      rpaProjectEnabled: initialRpaProjectEnabled = false,
      rpaStartFrom: initialRpaStartFrom = "",
      rpaTaxRateEnabled: initialRpaTaxRateEnabled = true
    } = {}
  } = businessData;

  const accountsData = useSelector(getAccountsData);

  const classes = useSelector(getSelectedBusinessClasses);

  const taxRates = useSelector(getSelectedBusinessTaxRates);

  const locations = useSelector(getSelectedBusinessLocations);

  const projects = useSelector(getSelectedBusinessProjects);

  const usCountry = countryCode === Countries.US;

  const accountNames = useMemo(() => {
    return accountsData
      .filter(({ extraData: { paymentAccount } = {} }) => !quickBooksBusiness || paymentAccount)
      .map(({ name }) => name.trim().replace(/\s+/g, " "));
  }, [quickBooksBusiness, accountsData]);

  const accountNameOptions = useMemo(() => {
    return accountNames.map((name) => ({ value: name, label: name }));
  }, [accountNames]);

  const bookCloseDate = useMemo(() => {
    return xeroBookCloseDate || quickBooksBookCloseDate || null;
  }, [xeroBookCloseDate, quickBooksBookCloseDate]);

  const minDate = useMemo(() => {
    return Businesses.getRpaStartFromMinDate(integrationService, rpaUpsellDate, businessData.createdAt);
  }, [integrationService, rpaUpsellDate, businessData.createdAt]);

  const initialRpaStartFromValue = useMemo(() => {
    if (initialRpaStartFrom) {
      return initialRpaStartFrom;
    }
    if (bookCloseDate) {
      return moment.max(moment.utc(bookCloseDate).add(1, "month").startOf("month"), minDate).format(DateTimeFormats.API_DATE);
    }

    return minDate.format(DateTimeFormats.API_DATE);
  }, [initialRpaStartFrom, bookCloseDate, minDate]);

  const integrationServiceLabel = useMemo(() => {
    const integrationServiceData = IntegrationServices.getByValue(integrationService);

    return integrationServiceData ? integrationServiceData.label : "";
  }, [integrationService]);

  const [
    titleText,
    excludeAccountsPlaceholderText,
    appsumoExtraText
  ] = messages.onboardingRpaSettings;

  const [matchTransactionsQboText, matchTransactionsXeroText] = messages.settingsMatchTransactions;

  const [matchTransactionsQboDescription, matchTransactionsXeroDescription] = messages.settingsMatchTransactionsDescription;

  const matchTransactionsText = useMemo(() => {
    return integrationService === IntegrationServices.QUICK_BOOKS.value ? matchTransactionsQboText : matchTransactionsXeroText;
  }, [integrationService, matchTransactionsQboText, matchTransactionsXeroText]);

  const matchTransactionsDescription = useMemo(() => {
    return integrationService === IntegrationServices.QUICK_BOOKS.value
      ? matchTransactionsQboDescription
      : matchTransactionsXeroDescription;
  }, [integrationService, matchTransactionsQboDescription, matchTransactionsXeroDescription]);

  const projectsToggleLabel = useMemo(() => {
    return projects.some(({ customer }) => customer)
      ? (projects.some(({ customer }) => !customer)
        ? messages.rpaProcessingProjectOrCustomerToggleLabelText
        : messages.rpaProcessingCustomerToggleLabelText)
      : messages.rpaProcessingProjectToggleLabelText;
  }, [messages, projects]);

  const projectsToggleDescription = useMemo(() => {
    return projects.some(({ customer }) => customer)
      ? (projects.some(({ customer }) => !customer)
        ? messages.rpaProcessingProjectOrCustomerToggleDescription
        : messages.rpaProcessingCustomerToggleDescription)
      : messages.rpaProcessingProjectToggleDescription;
  }, [messages, projects]);

  const tabKeys = useMemo(() => Object.values(TABS), []);

  const [activeTab, setActiveTab] = useState(TABS.GENERAL);

  const [fetching, setFetching] = useState(false);

  const [rpaAutoApprove, setRpaAutoApprove] = useState(true);

  const [rpaExcludedAccountsToggle, setRpaExcludedAccountsToggle] = useState(true);

  const [processExistingUncategorized, setProcessExistingUncategorized] = useState(false);

  const [rpaExcludedWords, setRpaExcludedWords] = useState([]);

  const [rpaExcludedWordsToggle, setRpaExcludedWordsToggle] = useState(true);

  const [allowAutoCreatePayee, setAllowAutoCreatePayee] = useState(true);

  const [allowApproveWithoutPayee, setAllowApproveWithoutPayee] = useState(true);

  const [rpaExcludedAccounts, setRpaExcludedAccounts] = useState([]);

  const [uncategorizedCategoryNames, setUncategorizedCategoryNames] = useState(
    businessData.settings?.uncategorizedCategoryNames || []
  );

  const [rpaTransactionTypes, setRpaTransactionTypes] = useState(RpaTransactionTypes.BOTH);

  const [rpaMatchingEnabled, setRpaMatchingEnabled] = useState(true);

  const [rpaClassEnabled, setRpaClassEnabled] = useState(initialRpaClassEnabled);

  const [rpaTaxRateEnabled, setRpaTaxRateEnabled] = useState(initialRpaTaxRateEnabled);

  const [rpaLocationEnabled, setRpaLocationEnabled] = useState(initialRpaLocationEnabled);

  const [rpaProjectEnabled, setRpaProjectEnabled] = useState(initialRpaProjectEnabled);

  const [rpaPayeeDocumentRule, setRpaPayeeDocumentRule] = useState(true);

  const [rpaAmountDocumentRuleEnabled, setRpaAmountDocumentRuleEnabled] = useState(false);

  const [rpaAmountDocumentRule, setRpaAmountDocumentRule] = useState("");

  const [rpaStartFrom, setRpaStartFrom] = useState();

  const effectiveRpaStartFrom = useMemo(() => {
    return rpaStartFrom === undefined ? initialRpaStartFromValue : rpaStartFrom;
  }, [initialRpaStartFromValue, rpaStartFrom]);

  const requiredFieldsAreValid = useMemo(() => {
    return Businesses.checkRpaSettingsRequiredFields({
      createdAt: businessData.createdAt,
      integrationService,
      rpaUpsellDate,
      rpaStartFrom: effectiveRpaStartFrom,
      rpaTransactionTypes,
      shouldRequireUncategorized: quickBooksBusiness
        && !ignoreUncategorizedTransactions
        && processExistingUncategorized,
      uncategorizedCategoryNames,
      shouldRequireExcludedAccounts: !rpaExcludedAccountsToggle,
      rpaExcludedAccounts,
      shouldRequireExcludedWords: !rpaExcludedWordsToggle,
      rpaExcludedWords,
      shouldRequireMinTransactionAmount: rpaAmountDocumentRuleEnabled,
      rpaAmountDocumentRule
    });
  }, [
    rpaTransactionTypes,
    rpaExcludedAccountsToggle,
    rpaExcludedAccounts,
    rpaExcludedWordsToggle,
    rpaExcludedWords,
    rpaAmountDocumentRuleEnabled,
    rpaAmountDocumentRule,
    rpaUpsellDate,
    integrationService,
    effectiveRpaStartFrom,
    businessData,
    quickBooksBusiness,
    ignoreUncategorizedTransactions,
    processExistingUncategorized,
    uncategorizedCategoryNames
  ]);

  const handleRpaAutoApproveToggleChange = useCallback((value) => {
    setRpaAutoApprove(value);
  }, []);

  /* TODO: const handleRpaAutoApproveDocumentsToggleChange = useCallback((value) => {
    setRpaAutoApproveDocuments(value);
  }, []); */

  const handleRpaExcludedAccountsToggleChange = useCallback((value) => {
    setRpaExcludedAccountsToggle(value);
    setRpaExcludedAccounts([]);
  }, []);

  const handleUncategorizedToggleChange = useCallback((value) => {
    setProcessExistingUncategorized(!value);
  }, []);

  const handleUncategorizedInputChange = useCallback((value) => {
    setUncategorizedCategoryNames(value);
  }, []);

  const handleRpaExcludedWordsToggleChange = useCallback((value) => {
    setRpaExcludedWordsToggle(value);
  }, []);

  const handleRpaExcludedWordsInputChange = useCallback((value) => {
    setRpaExcludedWords(value);
  }, []);

  const handleAllowApproveWithoutPayeeToggleChange = useCallback((value) => {
    setAllowApproveWithoutPayee(value);
  }, []);

  const handleAllowAutoCreatePayeeToggleChange = useCallback((value) => {
    setAllowAutoCreatePayee(value);
  }, []);

  const handleRpaExcludedAccountsInputChange = useCallback((value) => {
    setRpaExcludedAccounts(
      value.filter((item) => item && accountNames.includes(item))
    );
  }, [accountNames]);

  const handleWhoAskedButtonClick = useCallback((event, value) => {
    setRpaTransactionTypes(value);
  }, []);

  const handleRpaStartFromInputChange = useCallback((value) => {
    setRpaStartFrom(value);
  }, []);

  const handleRpaMatchingToggleChange = useCallback((value) => {
    setRpaMatchingEnabled(value);
  }, []);

  const handleRpaTaxRateToggleChange = useCallback((value) => {
    setRpaTaxRateEnabled(value);
  }, []);

  const handleRpaClassToggleChange = useCallback((value) => {
    setRpaClassEnabled(value);
  }, []);

  const handleRpaLocationToggleChange = useCallback((value) => {
    setRpaLocationEnabled(value);
  }, []);

  const handleRpaProjectToggleChange = useCallback((value) => {
    setRpaProjectEnabled(value);
  }, []);

  const handleRpaAmountDocumentRuleToggleChange = useCallback((value) => {
    setRpaAmountDocumentRuleEnabled(value);
  }, []);

  const handleRpaAmountDocumentRuleInputChange = useCallback((value) => {
    setRpaAmountDocumentRule(value);
  }, []);

  const handleRpaPayeeDocumentRuleToggleChange = useCallback((value) => {
    setRpaPayeeDocumentRule(value);
  }, []);

  const handleContinueButtonClick = useCallback(async() => {
    const activeTabIndex = tabKeys.indexOf(activeTab);

    if (activeTabIndex !== tabKeys.length - 1) {
      setActiveTab(tabKeys[activeTabIndex + 1]);

      return;
    }

    const payload = {
      rpaAutoApprove,
      rpaExcludedAccounts,
      rpaTransactionTypes,
      allowAutoCreatePayee,
      rpaMatchingEnabled,
      rpaClassEnabled,
      rpaLocationEnabled,
      rpaProjectEnabled,
      rpaTaxRateEnabled,
      rpaPayeeDocumentRule,
      rpaAmountDocumentRule: rpaAmountDocumentRuleEnabled
        ? +rpaAmountDocumentRule
        : false,
      rpaExcludedWords: rpaExcludedWordsToggle ? [] : rpaExcludedWords,
      ...(quickBooksBusiness && !ignoreUncategorizedTransactions && {
        uncategorizedCategoryNames: processExistingUncategorized
          ? uncategorizedCategoryNames
          : []
      }),
      allowApproveWithoutPayee,
      rpaStartFrom: Dates.formatNoTimeZoneDate(effectiveRpaStartFrom)
    };

    setFetching(true);
    await dispatch(
      BusinessesActions.editBusinessSettings(
        selectedBusinessId,
        payload,
        true
      )
    );
    setFetching(false);
  }, [
    rpaAutoApprove,
    rpaExcludedWords,
    rpaMatchingEnabled,
    rpaClassEnabled,
    rpaLocationEnabled,
    rpaProjectEnabled,
    rpaTaxRateEnabled,
    rpaPayeeDocumentRule,
    rpaAmountDocumentRuleEnabled,
    rpaAmountDocumentRule,
    rpaExcludedAccounts,
    rpaTransactionTypes,
    rpaExcludedWordsToggle,
    activeTab,
    tabKeys,
    effectiveRpaStartFrom,
    allowAutoCreatePayee,
    selectedBusinessId,
    quickBooksBusiness,
    allowApproveWithoutPayee,
    uncategorizedCategoryNames,
    processExistingUncategorized,
    ignoreUncategorizedTransactions,
    setActiveTab,
    dispatch
  ]);

  useEffect(() => {
    setRpaStartFrom(undefined);
  }, [selectedBusinessId]);

  const dateError = moment(effectiveRpaStartFrom).isBefore(minDate);

  return (
    <Step
      number={index + 1}
      className={Css.step}
      active={currentStep === index}
      success={currentStep > index}>
      <div className={ParentCss.row}>
        <div className={ParentCss.col}>
          <div className={ParentCss.subtitle}>{titleText}</div>
          {currentStep === index && (
            <>
              <Tabs
                className={Css.tabs}
                current={activeTab}
                onChange={setActiveTab}>
                {tabKeys.map((name) => (
                  <Tab key={name} name={name}>{uiTexts[name]}</Tab>
                ))}
              </Tabs>
              {(() => {
                switch (activeTab) {
                  case TABS.GENERAL:
                    return (
                      <div className={Css.tabContent} key={TABS.GENERAL}>
                        <div className={ParentCss.buttons}>
                          <div className={ParentCss.block}>
                            <div className={`${ParentCss.label} ${Css.topLevelLabel}`}>{messages.chooseTransactionsType}</div>
                            <div className={ParentCss.row}>
                              <Button
                                outline large block
                                loading={fetching}
                                primary={rpaTransactionTypes === RpaTransactionTypes.BOTH}
                                value={RpaTransactionTypes.BOTH}
                                className={ParentCss.button}
                                onClick={handleWhoAskedButtonClick}>
                                <span>{uiTexts.incomeAndExpenses}</span>
                              </Button>
                              <Button
                                outline large block
                                loading={fetching}
                                primary={rpaTransactionTypes === RpaTransactionTypes.INCOME}
                                value={RpaTransactionTypes.INCOME}
                                className={ParentCss.button}
                                onClick={handleWhoAskedButtonClick}>
                                <span>{uiTexts.incomeOnly}</span>
                              </Button>
                              <Button
                                outline large block
                                loading={fetching}
                                primary={rpaTransactionTypes === RpaTransactionTypes.EXPENSES}
                                value={RpaTransactionTypes.EXPENSES}
                                className={ParentCss.button}
                                onClick={handleWhoAskedButtonClick}>
                                <span>{uiTexts.expensesOnly}</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className={Css.startFrom}>
                          <div className={`${Css.label} ${Css.topLevelLabel}`}>{messages.onboardingRpaStartFrom}</div>
                          <div className={ParentCss.row}>
                            <div className={ParentCss.col}>
                              <div className={Css.dateWrap}>
                                <Tooltip className={Css.tooltip} opened={dateError}>
                                  <Icons.Warning />
                                  <span>
                                    {Texts.replaceVars(
                                      messages.rpaMinDateWarning, { date: minDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT) }
                                    )}
                                  </span>
                                </Tooltip>
                                <DatePickerInput
                                  monthOnly
                                  value={effectiveRpaStartFrom}
                                  minDate={minDate}
                                  disabledMonthMessage={quickBooksBusiness ? messages.onboardingRpaStartFromNote : null}
                                  error={!effectiveRpaStartFrom || dateError}
                                  invalid={!effectiveRpaStartFrom || dateError}
                                  placeholder={messages.onboardingRpaStartFromPlaceholder}
                                  onChange={handleRpaStartFromInputChange} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  case TABS.PROCESSING:
                    return (
                      <div className={Css.tabContent} key={TABS.PROCESSING}>
                        {quickBooksBusiness && !ignoreUncategorizedTransactions && (
                          <>
                            <div className={ParentCss.buttons}>
                              <div>
                                <Checkbox
                                  toggle
                                  checked={!processExistingUncategorized}
                                  onChange={handleUncategorizedToggleChange}>
                                  {messages.uncategorizedToggleLabelText}
                                </Checkbox>
                                <div className={Css.description}>{messages.uncategorizedToggleDescription}</div>
                              </div>
                            </div>
                            {processExistingUncategorized && (
                              <>
                                <div className={`${ParentCss.buttons} ${Css.toggleRelatedContent}`}>
                                  <div className={ParentCss.block}>
                                    <div className={ParentCss.label}>{messages.settingsUncategorizedLabel}</div>
                                    <SelectUncategorizedCategoryNames
                                      className={Css.tagsInput}
                                      value={uncategorizedCategoryNames}
                                      invalid={!uncategorizedCategoryNames.length}
                                      onChange={handleUncategorizedInputChange} />
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        )}
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaExcludedAccountsToggle}
                              onChange={handleRpaExcludedAccountsToggleChange}>
                              {messages.processAllAccountsToggleLabelText}
                            </Checkbox>
                            <div className={Css.description}>{messages.processAllAccountsToggleDescription}</div>
                          </div>
                        </div>
                        {!rpaExcludedAccountsToggle && (
                          <div className={`${ParentCss.buttons} ${Css.toggleRelatedContent}`}>
                            <div className={ParentCss.block}>
                              <div className={ParentCss.label}>{messages.settingsExcludedAccountsText}</div>
                              <TagsInput
                                className={Css.tagsInput}
                                value={rpaExcludedAccounts}
                                invalid={!rpaExcludedAccounts.length}
                                autoCompleteData={accountNameOptions}
                                onChange={handleRpaExcludedAccountsInputChange}
                                placeholder={excludeAccountsPlaceholderText} />
                            </div>
                          </div>
                        )}
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaExcludedWordsToggle}
                              onChange={handleRpaExcludedWordsToggleChange}>
                              {messages.processAllTransactionsToggleLabelText}
                            </Checkbox>
                            <div className={Css.description}>{messages.processAllTransactionsToggleDescription}</div>
                          </div>
                        </div>
                        {!rpaExcludedWordsToggle && (
                          <>
                            <div className={`${ParentCss.buttons} ${Css.toggleRelatedContent}`}>
                              <div className={ParentCss.block}>
                                <div className={ParentCss.label}>{messages.rpaExcludedWordsLabel}</div>
                                <div className={Css.excludeWords}>
                                  <TagsInput
                                    value={rpaExcludedWords}
                                    className={Css.tagsInput}
                                    invalid={!rpaExcludedWords?.length}
                                    placeholder={messages.rpaExcludedWordsPlaceholder}
                                    onChange={handleRpaExcludedWordsInputChange} />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {!usCountry && !!taxRates.length && (
                          <div className={ParentCss.buttons}>
                            <div>
                              <Checkbox
                                toggle
                                checked={rpaTaxRateEnabled}
                                onChange={handleRpaTaxRateToggleChange}>
                                {messages.rpaProcessingTaxRateToggleLabelText}
                              </Checkbox>
                              <div className={Css.description}>{messages.rpaProcessingTaxRateToggleDescription}</div>
                            </div>
                          </div>
                        )}
                        {!!classes.length && (
                          <div className={ParentCss.buttons}>
                            <div>
                              <Checkbox
                                toggle
                                checked={rpaClassEnabled}
                                onChange={handleRpaClassToggleChange}>
                                {messages.rpaProcessingClassToggleLabelText}
                              </Checkbox>
                              <div className={Css.description}>{messages.rpaProcessingClassToggleDescription}</div>
                            </div>
                          </div>
                        )}
                        {!!locations.length && (
                          <div className={ParentCss.buttons}>
                            <div>
                              <Checkbox
                                toggle
                                checked={rpaLocationEnabled}
                                onChange={handleRpaLocationToggleChange}>
                                {messages.rpaProcessingLocationToggleLabelText}
                              </Checkbox>
                              <div className={Css.description}>{messages.rpaProcessingLocationToggleDescription}</div>
                            </div>
                          </div>
                        )}
                        {!!projects.length && (
                          <div className={ParentCss.buttons}>
                            <div>
                              <Checkbox
                                toggle
                                checked={rpaProjectEnabled}
                                onChange={handleRpaProjectToggleChange}>
                                {projectsToggleLabel}
                              </Checkbox>
                              <div className={Css.description}>{projectsToggleDescription}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  case TABS.DOCUMENTS:
                    return (
                      <div className={Css.tabContent} key={TABS.DOCUMENTS}>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaPayeeDocumentRule}
                              onChange={handleRpaPayeeDocumentRuleToggleChange}>{messages.rpaPayeeDocumentRule}</Checkbox>
                            <div className={Css.description}>{messages.rpaPayeeDocumentRuleDescription}</div>
                          </div>
                        </div>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaAmountDocumentRuleEnabled}
                              onChange={handleRpaAmountDocumentRuleToggleChange}>
                              {messages.rpaAmountDocumentRule}
                            </Checkbox>
                            <div className={Css.description}>{messages.rpaAmountDocumentRuleDescription}</div>
                          </div>
                        </div>
                        {!!rpaAmountDocumentRuleEnabled && (
                          <div className={`${ParentCss.buttons} ${Css.toggleRelatedContent}`}>
                            <div className={ParentCss.block}>
                              <div className={ParentCss.label}>{messages.rpaMinTransactionAmountLabel}</div>
                              <Input
                                type="number"
                                value={rpaAmountDocumentRule}
                                className={Css.tagsInput}
                                invalid={rpaAmountDocumentRule === ""}
                                placeholder={uiTexts.enterMinimalTransactionAmount}
                                onChange={handleRpaAmountDocumentRuleInputChange} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  case TABS.AUTOMATION:
                    return (
                      <div className={Css.tabContent} key={TABS.AUTOMATION}>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaAutoApprove}
                              onChange={handleRpaAutoApproveToggleChange}>
                              {Texts.replaceVars(
                                messages.autoExportReadyEntriesToggleLabelText,
                                { system: integrationServiceLabel }
                              )}
                            </Checkbox>
                            <div className={Css.description}>
                              {Texts.replaceVars(
                                messages.autoExportReadyEntriesToggleDescription,
                                { system: integrationServiceLabel }
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={rpaMatchingEnabled}
                              onChange={handleRpaMatchingToggleChange}>
                              {matchTransactionsText}
                            </Checkbox>
                            <div className={Css.description}>{matchTransactionsDescription}</div>
                          </div>
                        </div>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={allowApproveWithoutPayee}
                              onChange={handleAllowApproveWithoutPayeeToggleChange}>
                              {Texts.replaceVars(
                                messages.approveWithoutPayeeToggleLabelText,
                                { system: integrationServiceLabel }
                              )}
                            </Checkbox>
                            <div className={Css.description}>
                              {Texts.replaceVars(
                                messages.approveWithoutPayeeToggleDescription,
                                { system: integrationServiceLabel }
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={ParentCss.buttons}>
                          <div>
                            <Checkbox
                              toggle
                              checked={allowAutoCreatePayee}
                              onChange={handleAllowAutoCreatePayeeToggleChange}>
                              {messages.autoCreatePayeeToggleLabelText}
                            </Checkbox>
                            <div className={Css.description}>{messages.autoCreatePayeeToggleDescription}</div>
                          </div>
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })()}
              <div className={ParentCss.row}>
                <div className={ParentCss.col} />
                <Button
                  primary large block
                  loading={fetching}
                  disabled={!requiredFieldsAreValid}
                  className={classNames(ParentCss.likeButton, Css.likeButton)}
                  onClick={handleContinueButtonClick}>{uiTexts.continue}</Button>
              </div>
            </>
          )}
        </div>
        {currentStep > index && (
          <div className={`${ParentCss.likeButton} ${ParentCss.success}`}>
            <Icons.Check weight="bold" />
            <span>{uiTexts.selected}</span>
          </div>
        )}
      </div>
      {currentStep === index && !!appsumoValue && (
        <div className={ParentCss.warning}>
          <Icons.WarningCircle />
          <span className={ParentCss.text}>{Texts.replaceVars(appsumoExtraText, { months: appsumoValue })}</span>
        </div>
      )}
    </Step>
  );
};

export default React.memo(RpaSettingsStep);
